import { useState } from "react";
import {
  BooleanField,
  Button,
  CreateButton,
  Datagrid,
  DateField,
  DeleteButton,
  FunctionField,
  List,
  TextField,
  TopToolbar,
  useNotify,
  useRecordContext,
  useRefresh,
} from "react-admin";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyIcon from "@mui/icons-material/VpnKey";
import SendIcon from "@mui/icons-material/Send";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { Chip, Stack } from "@mui/material";

import { adminApi } from "../../dataProvider";
import { SendDialog } from "./SendDialog";
import { ShowKeyDialog } from "./ShowKeyDialog";

type Wallet = {
  id: string;
  address: string;
  label: string | null;
  is_primary: boolean;
  is_active: boolean;
  balance_usdt: string;
  balance_trx: string;
  last_synced_at: string | null;
};

const RowActions = ({
  onShowKey,
  onSend,
}: {
  onShowKey: (w: Wallet) => void;
  onSend: (w: Wallet) => void;
}) => {
  const record = useRecordContext<Wallet>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState<string | null>(null);

  if (!record) return null;

  const run = async (action: string, fn: () => Promise<void>) => {
    setLoading(action);
    try {
      await fn();
      refresh();
    } catch (e) {
      notify(String(e), { type: "error" });
    } finally {
      setLoading(null);
    }
  };

  const setPrimary = () =>
    run("primary", async () => {
      await adminApi(`/admin/wallets/${record.id}/set-primary`, { method: "POST" });
      notify("Назначен primary", { type: "success" });
    });
  const refreshBal = () =>
    run("refresh", async () => {
      await adminApi(`/admin/wallets/${record.id}/refresh-balance`, { method: "POST" });
      notify("Баланс обновлён", { type: "success" });
    });

  return (
    <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
      {!record.is_primary && record.is_active && (
        <Button
          label="Primary"
          onClick={setPrimary}
          disabled={loading === "primary"}
          startIcon={<StarBorderIcon />}
        />
      )}
      <Button
        label="Refresh"
        onClick={refreshBal}
        disabled={loading === "refresh"}
        startIcon={<RefreshIcon />}
      />
      <Button label="Key" onClick={() => onShowKey(record)} startIcon={<KeyIcon />} />
      {record.is_primary && (
        <Button label="Send" onClick={() => onSend(record)} startIcon={<SendIcon />} />
      )}
      {!record.is_primary && (
        <DeleteButton
          label="Delete"
          mutationMode="pessimistic"
          confirmTitle="Удалить кошелёк?"
          confirmContent="Действие необратимо. Доступно только для кошельков без транзакций."
        />
      )}
    </Stack>
  );
};

const PrimaryChip = () => {
  const record = useRecordContext<Wallet>();
  if (!record) return null;
  if (record.is_primary)
    return <Chip size="small" color="success" icon={<StarIcon />} label="Primary" />;
  if (!record.is_active) return <Chip size="small" label="Inactive" />;
  return <Chip size="small" variant="outlined" label="Hot" />;
};

const Actions = () => (
  <TopToolbar>
    <CreateButton label="Создать кошель" />
  </TopToolbar>
);

export function WalletList() {
  const [sendFor, setSendFor] = useState<Wallet | null>(null);
  const [keyFor, setKeyFor] = useState<Wallet | null>(null);
  return (
    <>
      <List actions={<Actions />} perPage={25} sort={{ field: "created_at", order: "DESC" }}>
        <Datagrid bulkActionButtons={false} rowClick="edit">
          <PrimaryChip />
          <TextField source="label" />
          <TextField source="address" sx={{ fontFamily: "monospace", fontSize: 12 }} />
          <FunctionField label="USDT" render={(r: Wallet) => Number(r.balance_usdt).toFixed(2)} />
          <FunctionField label="TRX" render={(r: Wallet) => Number(r.balance_trx).toFixed(2)} />
          <BooleanField source="is_active" />
          <DateField source="last_synced_at" showTime />
          <DateField source="created_at" />
          <RowActions onShowKey={setKeyFor} onSend={setSendFor} />
        </Datagrid>
      </List>
      {sendFor && <SendDialog wallet={sendFor} onClose={() => setSendFor(null)} />}
      {keyFor && <ShowKeyDialog wallet={keyFor} onClose={() => setKeyFor(null)} />}
    </>
  );
}
